import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import * as api from '../../db/api.js'
import '../comercial/clientes.css'
import '../financeiro/contas.css'
import '../comercial/pipelines.css'
import './admin.css'

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'chart' },
  { id: 'users', label: 'Usuarios', icon: 'users' },
  { id: 'organizations', label: 'Organizacoes', icon: 'briefcase' },
  { id: 'clients', label: 'Clientes', icon: 'folder' },
  { id: 'invites', label: 'Convites', icon: 'key' },
  { id: 'permissions', label: 'Permissoes', icon: 'sliders' },
  { id: 'audit', label: 'Auditoria', icon: 'timeline' },
]

const roleOptions = ['owner', 'admin', 'manager', 'viewer', 'comum']
const clientRoleOptions = ['admin', 'editor', 'viewer']

const initials = (name) => String(name || '?')
  .trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'

function fmtDate(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function Pill({ tone = 'neutral', children }) {
  return <span className={`cl-pill admin-pill ${tone}`}>{children}</span>
}

function statusPill(active) {
  return active ? <Pill tone="saudavel">Ativo</Pill> : <Pill tone="inativo">Inativo</Pill>
}

function inviteTone(status) {
  if (status === 'active' || status === 'available') return 'saudavel'
  if (status === 'expired') return 'atencao'
  if (status === 'used') return 'neutral'
  return 'inativo'
}

function Kpi({ icon, color, bg, label, value, foot, onClick }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag type={onClick ? 'button' : undefined} className={`cl-kpi${onClick ? '' : ' static'}`} onClick={onClick}>
      <div className="cl-kpi-body">
        <span className="cl-kpi-icon" style={{ background: bg, color }}>
          <Icon n={icon} s={19} sw={1.7} />
        </span>
        <div className="cl-kpi-text">
          <div className="cl-kpi-label">{label}</div>
          <div className="cl-kpi-value">{value ?? 0}</div>
        </div>
      </div>
      {foot && <div className="cl-kpi-foot">{foot}</div>}
    </Tag>
  )
}

function Search({ value, onChange, placeholder, onSubmit }) {
  return (
    <form className="fc-search" onSubmit={(e) => { e.preventDefault(); onSubmit?.() }}>
      <span className="fc-search-icon"><Icon n="search" s={15} /></span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </form>
  )
}

function DataTable({ columns, rows, empty, onRowClick, selectedId }) {
  if (!rows?.length) return <div className="cl-state">{empty || 'Nenhum registro encontrado.'}</div>
  return (
    <div className="cl-table-scroll">
      <table className="cl-table admin-cl-table">
        <thead>
          <tr>{columns.map((c) => <th key={c.key} className={c.className || ''}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={selectedId === row.id ? 'active' : ''} onClick={() => onRowClick?.(row)}>
              {columns.map((c) => (
                <td key={c.key} className={c.className || ''}>
                  {c.render ? c.render(row) : (row[c.key] ?? '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminAvatar({ row }) {
  const name = row.full_name || row.email || row.id
  return (
    <div className="cl-client">
      <span className="cl-avatar-wrap">
        <span className="cl-avatar">{initials(name)}</span>
        {row.is_active && <span className="cl-avatar-dot" />}
      </span>
      <span className="admin-user-cell">
        <span className="cl-client-name">{name}</span>
        <span className="cl-cardcli-sub">{row.email || row.id}</span>
      </span>
    </div>
  )
}

function AdminLogin({ onLoggedIn }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function submit(e) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await api.adminLogin({ username, password })
      onLoggedIn()
    } catch (err) {
      setError(err.message || 'Falha no login administrativo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="admin-login-page">
      <form className="admin-login-card" onSubmit={submit}>
        <div className="admin-login-mark"><Icon n="crown" s={22} /></div>
        <h1>Admin</h1>
        <p>Acesso administrativo independente.</p>
        {error && <div className="fc-error">{error}</div>}
        <div className="fc-field">
          <label className="fc-label">Usuario</label>
          <input className="fc-input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
        </div>
        <div className="fc-field">
          <label className="fc-label">Senha</label>
          <input className="fc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </div>
        <button className="fc-ctrl primary admin-login-submit" disabled={loading} type="submit">
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </form>
    </div>
  )
}

function AccessDrawer({ detail, orgs, clients, onClose, onRefresh, notify }) {
  const [orgForm, setOrgForm] = useState({ organization_id: '', role: 'viewer', is_active: true })
  const [clientForm, setClientForm] = useState({ client_id: '', role: 'viewer', is_active: true })

  if (!detail) return null

  async function saveMembership() {
    if (!orgForm.organization_id) return
    await api.adminUpsertMembership(detail.user.id, orgForm)
    notify('Acesso de organizacao atualizado.')
    onRefresh()
  }

  async function saveClientAccess() {
    if (!clientForm.client_id) return
    await api.adminUpsertClientAccess(detail.user.id, clientForm)
    notify('Acesso de cliente atualizado.')
    onRefresh()
  }

  return (
    <>
      <div className="fc-drawer-overlay" onMouseDown={onClose} />
      <aside className="fc-drawer admin-drawer" onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-drawer-head">
          <span className="cl-avatar-wrap">
            <span className="cl-avatar">{initials(detail.user.full_name || detail.user.email)}</span>
            {detail.user.is_active && <span className="cl-avatar-dot" />}
          </span>
          <div className="admin-drawer-title">
            <span className="fc-drawer-title">{detail.user.full_name || detail.user.email || detail.user.id}</span>
            <span>{detail.user.email || detail.user.id}</span>
          </div>
          <button className="fc-drawer-x" type="button" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>

        <div className="fc-drawer-body">
          <div className="admin-drawer-badges">{statusPill(detail.user.is_active)}</div>

          <div className="cl-panel">
            <div className="cl-panel-title">Organizacoes</div>
            <div className="admin-access-list">
              {detail.memberships.length ? detail.memberships.map((m) => (
                <div className="admin-access-row" key={m.id}>
                  <div>
                    <strong>{m.organization_name}</strong>
                    <span>{m.organization_id}</span>
                  </div>
                  <div className="admin-access-meta">
                    <Pill tone="neutral">{m.role}</Pill>
                    {statusPill(m.is_active)}
                  </div>
                </div>
              )) : <div className="cl-empty-side">Sem organizacao vinculada.</div>}
            </div>

            <div className="fc-field">
              <label className="fc-label">Organizacao</label>
              <select className="fc-select" value={orgForm.organization_id} onChange={(e) => setOrgForm((p) => ({ ...p, organization_id: e.target.value }))}>
                <option value="">Selecione</option>
                {orgs.map((o) => <option value={o.id} key={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="fc-grid-2">
              <div className="fc-cell">
                <label className="fc-label">Nivel</label>
                <select className="fc-select" value={orgForm.role} onChange={(e) => setOrgForm((p) => ({ ...p, role: e.target.value }))}>
                  {roleOptions.map((r) => <option value={r} key={r}>{r}</option>)}
                </select>
              </div>
              <div className="fc-cell">
                <label className="fc-label">Status</label>
                <select className="fc-select" value={orgForm.is_active ? '1' : '0'} onChange={(e) => setOrgForm((p) => ({ ...p, is_active: e.target.value === '1' }))}>
                  <option value="1">Ativo</option>
                  <option value="0">Inativo</option>
                </select>
              </div>
            </div>
            <button className="fc-ctrl primary admin-save-btn" type="button" onClick={saveMembership}>Salvar organizacao</button>
          </div>

          <div className="cl-panel">
            <div className="cl-panel-title">Clientes</div>
            <div className="admin-access-list">
              {detail.clients.length ? detail.clients.map((c) => (
                <div className="admin-access-row" key={c.id}>
                  <div>
                    <strong>{c.client_name}</strong>
                    <span>{c.organization_name}</span>
                  </div>
                  <div className="admin-access-meta">
                    <Pill tone="neutral">{c.role}</Pill>
                    {statusPill(c.is_active)}
                  </div>
                </div>
              )) : <div className="cl-empty-side">Sem cliente concedido diretamente.</div>}
            </div>

            <div className="fc-field">
              <label className="fc-label">Cliente</label>
              <select className="fc-select" value={clientForm.client_id} onChange={(e) => setClientForm((p) => ({ ...p, client_id: e.target.value }))}>
                <option value="">Selecione</option>
                {clients.map((c) => <option value={c.id} key={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="fc-grid-2">
              <div className="fc-cell">
                <label className="fc-label">Nivel</label>
                <select className="fc-select" value={clientForm.role} onChange={(e) => setClientForm((p) => ({ ...p, role: e.target.value }))}>
                  {clientRoleOptions.map((r) => <option value={r} key={r}>{r}</option>)}
                </select>
              </div>
              <div className="fc-cell">
                <label className="fc-label">Status</label>
                <select className="fc-select" value={clientForm.is_active ? '1' : '0'} onChange={(e) => setClientForm((p) => ({ ...p, is_active: e.target.value === '1' }))}>
                  <option value="1">Ativo</option>
                  <option value="0">Inativo</option>
                </select>
              </div>
            </div>
            <button className="fc-ctrl primary admin-save-btn" type="button" onClick={saveClientAccess}>Salvar cliente</button>
          </div>
        </div>
      </aside>
    </>
  )
}

export default function AdminPanel() {
  const navigate = useNavigate()
  const location = useLocation()
  const [checkingAuth, setCheckingAuth] = useState(true)
  const [admin, setAdmin] = useState(null)
  const [tab, setTab] = useState('dashboard')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [overview, setOverview] = useState(null)
  const [users, setUsers] = useState([])
  const [orgs, setOrgs] = useState([])
  const [clients, setClients] = useState([])
  const [invites, setInvites] = useState([])
  const [accessInvites, setAccessInvites] = useState([])
  const [audit, setAudit] = useState([])
  const [q, setQ] = useState('')
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [userDetail, setUserDetail] = useState(null)
  const [inviteOrg, setInviteOrg] = useState('')
  const [accessInviteBusy, setAccessInviteBusy] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [toasts, setToasts] = useState([])

  const selectedUser = useMemo(() => users.find((u) => u.id === selectedUserId), [users, selectedUserId])

  function notify(message) {
    const id = Date.now()
    setToasts((prev) => [...prev, { id, message }])
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500)
  }

  async function checkAdminSession() {
    setCheckingAuth(true)
    try {
      const res = await api.adminMe()
      setAdmin(res.admin)
      if (location.pathname === '/admin/login') navigate('/admin', { replace: true })
    } catch {
      setAdmin(null)
      if (location.pathname !== '/admin/login') navigate('/admin/login', { replace: true })
    } finally {
      setCheckingAuth(false)
    }
  }

  useEffect(() => {
    checkAdminSession()
  }, [])

  async function loadAll() {
    setLoading(true)
    setError('')
    try {
      const [ov, us, os, cs, ins, accessIns, au] = await Promise.all([
        api.adminOverview(),
        api.adminListUsers(q),
        api.adminListOrganizations(),
        api.adminListClients(),
        api.adminListInvites(),
        api.adminListAccessInvites(),
        api.adminListAudit(),
      ])
      setOverview(ov)
      setUsers(us || [])
      setOrgs(os || [])
      setClients(cs || [])
      setInvites(ins || [])
      setAccessInvites(accessIns || [])
      setAudit(au || [])
    } catch (err) {
      setError(err.message || 'Falha ao carregar painel administrativo.')
      if (String(err.message || '').toLowerCase().includes('administrativ')) {
        setAdmin(null)
        navigate('/admin/login', { replace: true })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (admin) loadAll()
  }, [admin])

  async function loadUser(userId = selectedUserId) {
    if (!userId) return
    const detail = await api.adminGetUser(userId)
    setUserDetail(detail)
  }

  useEffect(() => {
    if (selectedUserId && admin) loadUser(selectedUserId)
  }, [selectedUserId, admin])

  async function openUser(row) {
    setSelectedUserId(row.id)
    setUserDetail(null)
    setDrawerOpen(true)
    await loadUser(row.id)
  }

  async function toggleUserActive(user) {
    await api.adminSetUserActive(user.id, !user.is_active)
    notify('Status do usuario atualizado.')
    await loadAll()
    if (selectedUserId === user.id) await loadUser(user.id)
  }

  async function toggleOrgActive(org) {
    await api.adminSetOrganizationActive(org.id, !org.is_active)
    notify('Organizacao atualizada.')
    await loadAll()
  }

  async function toggleClientActive(client) {
    await api.adminSetClientActive(client.id, !client.is_active)
    notify('Cliente atualizado.')
    await loadAll()
  }

  async function createInvite() {
    if (!inviteOrg) return
    await api.adminCreateInvite({ organizationId: inviteOrg })
    notify('Convite criado.')
    await loadAll()
  }

  async function revokeInvite(invite) {
    await api.adminRevokeInvite(invite.id)
    notify('Convite desativado.')
    await loadAll()
  }

  async function createAccessInvite() {
    setAccessInviteBusy('create')
    setError('')
    try {
      await api.adminCreateAccessInvite()
      notify('Convite de acesso criado.')
      await loadAll()
    } catch (err) {
      setError(err.message || 'Falha ao criar convite de acesso.')
    } finally {
      setAccessInviteBusy(null)
    }
  }

  async function deleteAccessInvite(invite) {
    if (!window.confirm(`Apagar permanentemente o convite ${invite.code}?`)) return
    setAccessInviteBusy(invite.id)
    setError('')
    try {
      await api.adminDeleteAccessInvite(invite.id)
      notify('Convite de acesso apagado.')
      await loadAll()
    } catch (err) {
      setError(err.message || 'Falha ao apagar convite de acesso.')
    } finally {
      setAccessInviteBusy(null)
    }
  }

  function logout() {
    api.adminLogout()
    setAdmin(null)
    navigate('/admin/login', { replace: true })
  }

  if (checkingAuth) return <div className="admin-login-page"><div className="cl-state">Carregando...</div></div>

  if (!admin) {
    return <AdminLogin onLoggedIn={checkAdminSession} />
  }

  const totals = overview?.totals || {}
  const counts = {
    users: users.length,
    organizations: orgs.length,
    clients: clients.length,
    invites: invites.length + accessInvites.length,
    audit: audit.length,
  }

  const userColumns = [
    { key: 'full_name', label: 'Usuario', render: (r) => <AdminAvatar row={r} /> },
    { key: 'organizations_count', label: 'Orgs' },
    { key: 'clients_count', label: 'Clientes' },
    { key: 'is_active', label: 'Status', render: (r) => statusPill(r.is_active) },
    {
      key: 'actions',
      label: '',
      className: 'cl-col-acoes',
      render: (r) => (
        <div className="cl-row-actions admin-row-actions" onClick={(e) => e.stopPropagation()}>
          <button className="cl-icon-btn" type="button" title="Abrir usuario" onClick={() => openUser(r)}><Icon n="eye" s={15} /></button>
          <button className="cl-icon-btn" type="button" title={r.is_active ? 'Desativar' : 'Ativar'} onClick={() => toggleUserActive(r)}>
            <Icon n={r.is_active ? 'x' : 'check'} s={15} />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <span className="admin-brand-mark"><Icon n="crown" s={18} /></span>
          <span>Admin</span>
        </div>
        <nav className="admin-nav" aria-label="Navegacao administrativa">
          {TABS.map((item) => (
            <button key={item.id} type="button" className={`admin-nav-item${tab === item.id ? ' active' : ''}`} onClick={() => setTab(item.id)}>
              <Icon n={item.icon} s={17} />
              <span>{item.label}</span>
              {counts[item.id] != null && <span className="admin-nav-count">{counts[item.id]}</span>}
            </button>
          ))}
        </nav>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <div>
            <div className="tb-title">{TABS.find((t) => t.id === tab)?.label || 'Admin'}</div>
            <div className="tb-breadcrumb"><span>Ambiente administrativo</span></div>
          </div>
          <div className="admin-topbar-actions">
            <span className="admin-session">{admin.username}</span>
            <button className="pl-ghost-btn" type="button" onClick={logout}>
              <Icon n="x" s={15} /> Sair
            </button>
          </div>
        </header>

        <div className="admin-page-body">
          <div className="pl-toolbar admin-toolbar">
            <div className="pl-search-wrap">
              <Search value={q} onChange={setQ} onSubmit={loadAll} placeholder="Buscar usuarios por nome ou email..." />
            </div>
            <div className="pl-toolbar-right">
              {tab === 'invites' && (
                <>
                  <button className="pl-primary-btn" type="button" onClick={createAccessInvite} disabled={accessInviteBusy != null}>
                    <Icon n="plus" s={15} sw={2} /> {accessInviteBusy === 'create' ? 'Criando...' : 'Novo acesso'}
                  </button>
                  <select className="fc-ctrl" value={inviteOrg} onChange={(e) => setInviteOrg(e.target.value)}>
                    <option value="">Organizacao</option>
                    {orgs.map((o) => <option value={o.id} key={o.id}>{o.name}</option>)}
                  </select>
                  <button className="pl-ghost-btn" type="button" onClick={createInvite} disabled={!inviteOrg}>
                    <Icon n="plus" s={15} sw={2} /> Nova organizacao
                  </button>
                </>
              )}
              <button className="pl-ghost-btn" type="button" onClick={loadAll}>
                <Icon n="refresh" s={15} /> Atualizar
              </button>
            </div>
          </div>

          {error && <div className="fc-error">{error}</div>}

          {loading ? (
            <div className="cl-card"><div className="cl-state">Carregando...</div></div>
          ) : (
            <>
              {tab === 'dashboard' && (
                <>
                  <div className="cl-top">
                    <div className="cl-kpis">
                      <Kpi icon="users" color="#2563eb" bg="#eaf0fe" label="Usuarios" value={totals.users_total} foot={`${totals.users_active || 0} ativos`} onClick={() => setTab('users')} />
                      <Kpi icon="briefcase" color="#7c5cf0" bg="#f0edfe" label="Organizacoes" value={totals.organizations_total} foot={`${totals.organizations_active || 0} ativas`} onClick={() => setTab('organizations')} />
                      <Kpi icon="folder" color="#12a06a" bg="#e6f8f0" label="Clientes" value={totals.clients_total} foot={`${totals.clients_active || 0} ativos`} onClick={() => setTab('clients')} />
                      <Kpi icon="key" color="#f0a125" bg="#fef4e2" label="Convites" value={totals.invites_total} foot={`${totals.invites_active || 0} ativos`} onClick={() => setTab('invites')} />
                      <Kpi icon="timeline" color="#ef4b45" bg="#fdeeed" label="Auditoria" value={overview?.audit?.length || 0} foot="ultimas acoes" onClick={() => setTab('audit')} />
                    </div>
                  </div>

                  <div className="cl-grid">
                    <div className="cl-card">
                      <div className="admin-card-head">
                        <span>Usuarios recentes</span>
                        <button type="button" className="pl-link-btn" onClick={() => setTab('users')}>Ver todos</button>
                      </div>
                      <DataTable rows={overview?.recent_users || []} columns={userColumns} onRowClick={openUser} empty="Nenhum usuario encontrado." />
                    </div>

                    <aside className="cl-side">
                      <div className="cl-side-head">
                        <span className="cl-side-title">Auditoria recente</span>
                        <button type="button" className="cl-side-toggle" onClick={() => setTab('audit')}><Icon n="arrow_right" s={15} /></button>
                      </div>
                      <div className="cl-side-body">
                        <div className="cl-panel">
                          <div className="cl-panel-title">Ultimas acoes</div>
                          {(overview?.audit || []).length ? overview.audit.map((item) => (
                            <button key={item.id} type="button" className="cl-attn admin-audit-row" onClick={() => setTab('audit')}>
                              <span className="cl-attn-info">
                                <span className="cl-attn-name">{item.action}</span>
                                <span className="cl-attn-why">{item.actor_email || 'Admin'} - {fmtDate(item.created_at)}</span>
                              </span>
                              <span className="cl-attn-icon"><Icon n="timeline" s={16} /></span>
                            </button>
                          )) : <div className="cl-empty-side">Sem acoes registradas.</div>}
                        </div>
                      </div>
                    </aside>
                  </div>
                </>
              )}

              {tab === 'users' && (
                <div className="cl-card">
                  <DataTable rows={users} columns={userColumns} onRowClick={openUser} selectedId={selectedUserId} empty="Nenhum usuario encontrado." />
                </div>
              )}

              {tab === 'organizations' && (
                <div className="cl-card">
                  <DataTable rows={orgs} empty="Nenhuma organizacao encontrada." columns={[
                    { key: 'name', label: 'Organizacao', render: (r) => <div><div className="cl-client-name">{r.name}</div><div className="cl-cardcli-sub">{r.slug || r.id}</div></div> },
                    { key: 'type', label: 'Tipo' },
                    { key: 'users_count', label: 'Usuarios' },
                    { key: 'clients_count', label: 'Clientes' },
                    { key: 'is_active', label: 'Status', render: (r) => statusPill(r.is_active) },
                    { key: 'actions', label: '', className: 'cl-col-acoes', render: (r) => <button className="cl-icon-btn" type="button" title={r.is_active ? 'Desativar' : 'Ativar'} onClick={() => toggleOrgActive(r)}><Icon n={r.is_active ? 'x' : 'check'} s={15} /></button> },
                  ]} />
                </div>
              )}

              {tab === 'clients' && (
                <div className="cl-card">
                  <DataTable rows={clients} empty="Nenhum cliente encontrado." columns={[
                    { key: 'name', label: 'Cliente', render: (r) => <div><div className="cl-client-name">{r.name}</div><div className="cl-cardcli-sub">{r.domain || 'Sem dominio'}</div></div> },
                    { key: 'organization_name', label: 'Organizacao' },
                    { key: 'users_count', label: 'Usuarios' },
                    { key: 'is_self', label: 'Tipo', render: (r) => r.is_self ? <Pill tone="neutral">Proprio</Pill> : <span className="cl-proj-empty">Cliente</span> },
                    { key: 'is_active', label: 'Status', render: (r) => statusPill(r.is_active) },
                    { key: 'actions', label: '', className: 'cl-col-acoes', render: (r) => <button className="cl-icon-btn" type="button" title={r.is_active ? 'Desativar' : 'Ativar'} onClick={() => toggleClientActive(r)}><Icon n={r.is_active ? 'x' : 'check'} s={15} /></button> },
                  ]} />
                </div>
              )}

              {tab === 'invites' && (
                <div className="admin-invite-sections">
                  <div className="cl-card">
                    <div className="admin-card-head">
                      <div className="admin-card-title">
                        <span>Acesso a plataforma</span>
                        <small>Codigos TITAM usados para criar uma nova conta e organizacao.</small>
                      </div>
                    </div>
                    <DataTable rows={accessInvites} empty="Nenhum convite de acesso encontrado." columns={[
                      { key: 'code', label: 'Codigo', render: (r) => <span className="admin-code">{r.code}</span> },
                      { key: 'status', label: 'Status', render: (r) => <Pill tone={inviteTone(r.status)}>{r.status}</Pill> },
                      { key: 'use_count', label: 'Usos', render: (r) => `${r.use_count}/${r.max_uses}` },
                      { key: 'created_at', label: 'Criado em', render: (r) => fmtDate(r.created_at) },
                      { key: 'used_by_email', label: 'Usado por', render: (r) => r.used_by_email || <span className="cl-proj-empty">-</span> },
                      { key: 'actions', label: '', className: 'cl-col-acoes', render: (r) => <button className="cl-icon-btn" type="button" title="Apagar permanentemente" disabled={accessInviteBusy != null} onClick={() => deleteAccessInvite(r)}><Icon n="x" s={15} /></button> },
                    ]} />
                  </div>

                  <div className="cl-card">
                    <div className="admin-card-head">
                      <div className="admin-card-title">
                        <span>Convites de organizacao</span>
                        <small>Codigos usados para entrar em uma organizacao existente.</small>
                      </div>
                    </div>
                    <DataTable rows={invites} empty="Nenhum convite de organizacao encontrado." columns={[
                      { key: 'invite_code', label: 'Codigo', render: (r) => <span className="admin-code">{r.invite_code}</span> },
                      { key: 'organization_name', label: 'Organizacao' },
                      { key: 'status', label: 'Status', render: (r) => <Pill tone={inviteTone(r.status)}>{r.status}</Pill> },
                      { key: 'created_at', label: 'Criado em', render: (r) => fmtDate(r.created_at) },
                      { key: 'used_by_email', label: 'Usado por', render: (r) => r.used_by_email || <span className="cl-proj-empty">-</span> },
                      { key: 'actions', label: '', className: 'cl-col-acoes', render: (r) => r.status === 'active' ? <button className="cl-icon-btn" type="button" title="Desativar" onClick={() => revokeInvite(r)}><Icon n="x" s={15} /></button> : null },
                    ]} />
                  </div>
                </div>
              )}

              {tab === 'permissions' && (
                <div className="cl-card">
                  <div className="admin-card-head">
                    <span>Permissoes por usuario</span>
                    {selectedUser && <button className="pl-link-btn" type="button" onClick={() => setDrawerOpen(true)}>Abrir acessos</button>}
                  </div>
                  <DataTable rows={users} columns={userColumns} onRowClick={openUser} selectedId={selectedUserId} empty="Selecione um usuario para editar permissoes." />
                </div>
              )}

              {tab === 'audit' && (
                <div className="cl-card">
                  <DataTable rows={audit} empty="Nenhuma acao administrativa registrada." columns={[
                    { key: 'created_at', label: 'Data', render: (r) => fmtDate(r.created_at) },
                    { key: 'actor_email', label: 'Ator', render: (r) => r.actor_email || 'Admin' },
                    { key: 'action', label: 'Acao', render: (r) => <span className="admin-code">{r.action}</span> },
                    { key: 'target_type', label: 'Alvo' },
                    { key: 'target_id', label: 'ID', render: (r) => r.target_id || <span className="cl-proj-empty">-</span> },
                  ]} />
                </div>
              )}
            </>
          )}
        </div>

        {drawerOpen && userDetail && (
          <AccessDrawer detail={userDetail} orgs={orgs} clients={clients} onClose={() => setDrawerOpen(false)} onRefresh={() => loadUser(selectedUserId)} notify={notify} />
        )}

        <div className="toast-wrap">
          {toasts.map((t) => (
            <div key={t.id} className="toast"><Icon n="check" s={14} c="#22c55e" />{t.message}</div>
          ))}
        </div>
      </main>
    </div>
  )
}
